<script setup>
import { computed, useId } from 'vue';
import FieldError from './FieldError.vue';

/**
 * Control-agnostic form field wrapper: renders a label (+ required marker) and
 * the field's error, and slots in the actual control. Modeled on the asuready
 * FormField pattern, adapted to Vuetify + the app's manual error rendering.
 *
 * Usage:
 *   <FormField label="First Name" required :error="firstNameError" v-slot="{ isInvalid, ariaDescribedby, id }">
 *     <v-text-field :id="id" :error="isInvalid" :aria-describedby="ariaDescribedby" ... />
 *   </FormField>
 */
const props = defineProps({
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  // The error message to show (empty = valid). Consumers compute this.
  error: { type: String, default: '' },
  // Helper text under the control (e.g. "Name must match photo ID")
  hint: { type: String, default: '' },
  // Control id when the page needs a stable one; generated otherwise
  id: { type: String, default: '' }
});

const generatedId = useId();
const uid = computed(() => props.id || generatedId);
const errorId = computed(() => `${uid.value}-error`);
const isInvalid = computed(() => !!props.error);
const ariaDescribedby = computed(() => (props.error ? errorId.value : undefined));
</script>

<template>
  <div class="form-field">
    <label v-if="label" :for="uid" class="form-field__label mb-2">
      {{ label }}<span v-if="required" class="form-field__required" aria-hidden="true">*</span>
    </label>

    <slot v-bind="{ isInvalid, ariaDescribedby, id: uid }" />

    <p v-if="hint" class="form-field__hint">{{ hint }}</p>

    <FieldError :error="error" :id="errorId" />
  </div>
</template>

<style scoped>
.form-field__label {
  display: block;
  font-size: 16px;
  color: #191919;
}

.form-field__required {
  color: #cc2f2f;
  margin-left: 2px;
}

.form-field__hint {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 18px;
  color: var(--asu-grayscale-gray-3);
}
</style>
